<script setup>
import { ref, computed, useSanityQuery, useDevice } from '#imports';
import { getQuery as getAuthorsQuery } from '~/groq-queries/authors';
import { getQuery as getHiringCardQuery } from '~/groq-queries/hiring';

const { isMobile } = useDevice();

const mobileOffset = 8;
const mobileChunkSize = ref(mobileOffset);

const [{ data: authors }, { data: hiringCard }] = await Promise.all([
  useSanityQuery(getAuthorsQuery()),
  useSanityQuery(getHiringCardQuery()),
]);

const authorsSorted = computed(() =>
  [...authors.value].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
);
const authorsPrepared = computed(() =>
  isMobile
    ? authorsSorted.value.slice(0, mobileChunkSize.value)
    : authorsSorted.value
);
const isShowMoreButton = computed(
  () => authors.value.length >= mobileChunkSize.value && isMobile
);

function showMoreAuthors() {
  mobileChunkSize.value += mobileOffset;
}
</script>

<template>
  <div
    v-if="hiringCard || authors.length > 0"
    class="about-authors content-container"
  >
    <div v-if="authors.length > 0" class="about-authors__cards-wrapper">
      <img
        src="~/assets/images/authors-title.svg"
        alt="about title"
        class="about-authors__title"
      />

      <div class="about-authors__cards">
        <AboutAuthorsAuthor
          v-for="author in authorsPrepared"
          :key="author._id"
          :author="author"
        />
      </div>
      <div v-if="isShowMoreButton" class="about-authors__more">
        <MoreContentButton @click="showMoreAuthors" />
      </div>
    </div>
    <div v-if="hiringCard" class="about-authors__invitation">
      <img
        src="~/assets/images/invitation-title.svg"
        alt="invitation title"
        class="about-authors__invitation-title"
      />
      <img
        src="~/assets/images/invitation-title-mobile.svg"
        alt="invitation title"
        class="about-authors__invitation-title-mobile"
      />
      <div class="about-authors__invitation-text">
        <PostContent is-hiring :content="hiringCard.content" />
      </div>
      <BaseExternalLink
        href="mailto:join@doxa.team"
        class="about-authors__invitation-link"
      >
        join@doxa.team
      </BaseExternalLink>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.about-authors {
  &__cards-wrapper {
    padding-bottom: 60px;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;

    @media (--tablet-vp) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  &__title {
    width: 100%;
    margin-bottom: -32px;
  }

  &__more {
    display: flex;
    justify-content: center;
    padding: 32px 0;
  }

  &__invitation {
    min-width: 0;
  }

  &__invitation-title {
    width: 100%;
  }

  &__invitation-title-mobile {
    display: none;
    width: 100%;
    margin-bottom: 12px;
  }

  &__invitation-text {
    margin-bottom: 40px;
  }

  &__invitation-link {
    font-size: 80px;
    font-weight: 500;
    line-height: 98px;
  }

  @media (--small-vp) {
    &__cards-wrapper {
      padding-bottom: 40px;
    }

    &__cards {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    &__title {
      width: 100%;
      margin-bottom: 0;
    }

    &__invitation-title {
      display: none;
    }

    &__invitation-title-mobile {
      display: block;
    }

    &__invitation-text {
      margin-bottom: 22px;
      font-size: 24px;
      line-height: 30px;
    }

    &__invitation-link {
      font-size: 28px;
      line-height: 36px;
    }
  }
}
</style>
